import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FeaturedProduct } from "./types";

interface FeaturedSlideProps {
  product: FeaturedProduct;
  selectedScreenshotIndex?: number;
}

export function FeaturedSlide({ product, selectedScreenshotIndex = -1 }: FeaturedSlideProps) {
  // First previewImage is the main image, thumbnails start from index 1
  const mainImage = product.previewImages[0]?.imageUrl || product.featuredImageUrl;

  // Determine which image to display based on selected screenshot index
  // -1 means default (first previewImage), 0+ means show that thumbnail (which is previewImages[index + 1])
  const displayImage =
    selectedScreenshotIndex >= 0 && product.previewImages[selectedScreenshotIndex + 1]
      ? product.previewImages[selectedScreenshotIndex + 1].imageUrl
      : mainImage;

  return (
    <div className="relative h-full w-full">
      {/* Full-bleed image - takes most of the space */}
      <div className="relative w-full h-[400px] md:h-[540px] lg:h-[540px] overflow-hidden">
        <Image
          src={displayImage}
          alt={product.name}
          width={1280}
          height={720}
          className="w-full h-full object-cover"
          priority
          unoptimized={true}
          key={displayImage}
        />
      </div>

      {/* Minimal info bar at bottom */}
      <div className="absolute inset-x-0 bottom-0 bg-card border-border rounded-b-2xl">
        <div className="flex items-center justify-between gap-4 px-5 py-3">
          {/* Left: Logo + Product name */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg overflow-hidden border border-border flex-shrink-0 bg-background">
              <Image
                src={product.logoUrl}
                alt={`${product.name} logo`}
                width={32}
                height={32}
                className="w-full h-full object-contain"
                unoptimized={true}
              />
            </div>
            <h3 className="text-base font-semibold text-foreground truncate">
              {product.name}
            </h3>
          </div>

          {/* Right: CTA button */}
          <Link href={`/products/${product.slug}`}>
            <Button
              size="sm"
              variant="secondary"
              className="rounded-lg font-medium px-4"
            >
              Смотреть
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
